import Vue from 'vue';
import basic from './common/basic.js';
import updater from '../updater.js';

/* State example:
state: {
  byId: {
    "bo_0": {
      "collapsed": false,
    },
    "bo_1": {
      "collapsed": false,
    },
  },
  open: null,
  selections: [],
}
*/

const options = {};

const state = { byId: {}, open: null, selections: [] };

const getters = {
  ...basic.getters(options),

  /**
   * Return board's transform.
   * @param   {string} id    The board id
   * @return  {Object}       The transform object
   */
  getTransform(state) {
    return id => {
      return state.byId[id].transform;
    };
  },

  /**
   * Return which board is open.
   * @return {string|null}   The open board id (null if none)
   */
  getOpen(state) {
    return state.open;
  },
};

const actions = {
  ...basic.actions({ type: 'boards' }),

  /**
   * Create a configuration for default board.
   * @param   {string}  id                      The board id
   * @param   {newItem|newContainer} board      The board type
   * @param   {boolean} apply                   Whether to apply the patch after finishing
   */
  customCreate(context, { id, board, apply }) {
    context.dispatch('create', {
      node: {
        id,
        ...board.config,
      },
      apply: false,
    });
    updater.complete({ apply });
  },

  /**
   * Delete a configuration for the given board
   * @param {string}    id                    The board id
   * @param {boolean}   apply                 Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    context.dispatch('delete', { id, apply: false });
    updater.complete({ apply });
  },

  /**
   * Set a board's transform
   * @param {string}    id                    The board for which to set the transform
   * @param {Object}    transform             The transform object (x, y, scale)
   * @param {boolean}   apply                 Whether to apply the patch
   */
  setTransform(context, { id, transform, apply }) {
    const patch = [
      // Set the board transform
      {
        op: 'replace',
        path: `/config/boards/byId/${id}/transform`,
        value: transform,
      },
    ];
    updater.add(patch);
    updater.complete({ apply, undoable: false });
  },

  /**
   * Set the current open board
   * @param {string}    id            The open board
   * @param {boolean}   apply         Whether to apply the patch
   */
  setOpen(context, { id, apply }) {
    const patch = [
      // Add component data to byId
      {
        op: 'replace',
        path: `/config/boards/open`,
        value: id,
      },
    ];
    updater.add(patch);
    context.dispatch(
      'project/config/components/setOpen',
      {
        id: null,
        apply: false,
      },
      { root: true }
    );
    updater.complete({ apply });
  },
};

// TEST transform mutation
const mutations = {
  setTransform(state, { id, transform }) {
    state.byId[id].transform = transform;
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default module;
